import { useForm, Controller } from 'react-hook-form';
import { useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import PasswordField from '../../../components/ui/PasswordField';
import Button from '../../../components/ui/Button';

const defaultValues = {
  name: '',
  code: '',
  adminFirstName: '',
  adminLastName: '',
  adminEmail: '',
  adminPassword: '',
};

/**
 * Client-side password policy — mirrors the server (12–128, upper, lower, number).
 * Returns an error string or null when valid.
 */
export function validatePassword(value) {
  const pw = String(value || '');
  if (pw.length < 12) return 'Password must be at least 12 characters';
  if (pw.length > 128) return 'Password must be at most 128 characters';
  if (!/[A-Z]/.test(pw)) return 'Password must include at least one uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Password must include at least one lowercase letter';
  if (!/[0-9]/.test(pw)) return 'Password must include at least one number';
  return null;
}

/**
 * Create organisation + first admin. Owns its own react-hook-form instance.
 */
export default function CreateOrganizationModal({ isOpen, onClose, onSubmit, isSubmitting }) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (!isOpen) reset(defaultValues);
  }, [isOpen, reset]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const submit = (data) => {
    const payload = {
      name: data.name.trim(),
      adminFirstName: data.adminFirstName.trim(),
      adminLastName: data.adminLastName.trim(),
      adminEmail: data.adminEmail.trim(),
      adminPassword: data.adminPassword,
    };
    const code = (data.code || '').trim();
    if (code) payload.code = code;
    onSubmit(payload);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create organisation" className="team-invite-modal">
      <form onSubmit={handleSubmit(submit)} className="team-invite-form">
        <Input
          id="org-name"
          label="Organisation name"
          error={errors.name?.message}
          autoComplete="organization"
          {...register('name', { required: 'Organisation name is required' })}
        />
        <Input
          id="org-code"
          label="Code (optional)"
          error={errors.code?.message}
          placeholder="Short unique code"
          {...register('code')}
        />

        <p className="settings-section-hint">
          First admin — they will be asked to change this password on first login.
        </p>

        <div className="form-row">
          <Input
            id="admin-first-name"
            label="First name"
            error={errors.adminFirstName?.message}
            autoComplete="given-name"
            {...register('adminFirstName', { required: 'First name is required' })}
          />
          <Input
            id="admin-last-name"
            label="Last name"
            error={errors.adminLastName?.message}
            autoComplete="family-name"
            {...register('adminLastName', { required: 'Last name is required' })}
          />
        </div>

        <Input
          id="admin-email"
          label="Admin email"
          type="email"
          error={errors.adminEmail?.message}
          autoComplete="email"
          {...register('adminEmail', { required: 'Admin email is required' })}
        />

        <Controller
          name="adminPassword"
          control={control}
          rules={{
            required: 'Password is required',
            validate: (value) => validatePassword(value) || true,
          }}
          render={({ field }) => (
            <PasswordField
              id="admin-password"
              label="Temporary password"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              error={errors.adminPassword?.message}
              placeholder="12+ chars, upper, lower, number"
              autoComplete="new-password"
            />
          )}
        />

        <div className="form-actions">
          <Button type="button" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create organisation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
